import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, FileUp, List, Loader2, Network, Plus, Shield, Swords } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api } from "../../api/client";
import type { EnemyType, OdbDetail, OdbSide } from "../../api/types";
import { EmptyState } from "../../components/EmptyState";
import { libraryReferenceEnemy, useReferenceEnemy } from "../../enemyDisplay";
import { Motion, Presence } from "../../components/presence";
import { Button, IconButton } from "../../components/ui";
import { useDismiss } from "../../components/useDismiss";
import { useMediaQuery } from "../../components/useMediaQuery";
import { ViewHeader } from "../../components/ViewHeader";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { leaveWith, unsavedDrafts } from "../../state/unsavedDrafts";
import { NewOdbDialog } from "./NewOdbDialog";
import { OdbEditor } from "./OdbEditor";
import { OdbList, unitsEditable } from "./OdbList";
import { type OrbatsCreate, useOrbatsTarget } from "./orbatsTarget";

/** Vue Ordres de bataille : les ordres de bataille amis construits avec les symboles OTAN (APP-6) et leurs numéros,
 *  les ordres de bataille ennemis (symboles hostiles, échelons, fonctions, rôles dans la manœuvre), et ceux fournis
 *  avec Generex. Un ordre de bataille construit sert au moteur comme ceux du catalogue : force des Données
 *  initiales, dispositif, ordre. Liste à gauche (tiroir sous 1280 px), éditeur à droite. Ouverte depuis les Données
 *  initiales, elle peut préparer une création et proposer, une fois l'ordre de bataille enregistré, de l'employer
 *  dans le dossier. */

export function OrbatsView() {
  const setView = useUi((s) => s.setView);
  const exerciseId = useUi((s) => s.exerciseId);
  const openWelcome = useUi((s) => s.openWelcome);
  const notify = useUi((s) => s.notify);
  const queryClient = useQueryClient();
  const target = useOrbatsTarget();
  const [side, setSide] = useState<OdbSide>(() => target.side);
  const listing = useQuery({ queryKey: ["odbs", side], queryFn: () => api.odbs(side) });
  const vocabulary = useQuery({ queryKey: ["odb-vocabulary", side], queryFn: () => api.odbVocabulary(side), staleTime: Infinity });
  const amiVocabulary = useQuery({ queryKey: ["odb-vocabulary", "AMI"], queryFn: () => api.odbVocabulary("AMI"), staleTime: Infinity });
  const [selected, setSelected] = useState<string | null>(() => target.code);
  // Création demandée par le lien qui a ouvert la vue (Données initiales, palette de commandes).
  const [creating, setCreating] = useState<OrbatsCreate | true | null>(() => target.create);
  // Dossier d'où vient la demande, gardé tant que la vue est ouverte (la demande est consommée à sa fermeture).
  const [origin, setOrigin] = useState(() => target.origin);
  // Ouverte pour une création : aucun ordre de bataille n'est ouvert d'office derrière le dialogue. « Employer dans
  // le dossier » n'est proposé que pour un ordre de bataille choisi pendant cette visite (lien « Modifier », choix
  // dans la liste, création, copie, import), jamais pour le premier de la liste.
  const [autoOpen, setAutoOpen] = useState(() => !target.create);
  const [chosen, setChosen] = useState(() => Boolean(target.code));
  const handledTarget = useRef(target.at);
  useEffect(() => {
    if (handledTarget.current === target.at) return;
    handledTarget.current = target.at;
    setSide(target.side);
    setSelected(target.code);
    setOrigin(target.origin);
    setChosen(Boolean(target.code));
    if (target.create) {
      setAutoOpen(false);
      setCreating(target.create);
    }
  }, [target]);
  // La demande est consommée : une prochaine ouverture sans lien reprend les amis.
  useEffect(() => () => useOrbatsTarget.setState({ side: "AMI", code: null, create: null, origin: null }), []);
  const importInput = useRef<HTMLInputElement>(null);
  const wide = useMediaQuery("(min-width: 1280px)");
  const roomy = useMediaQuery("(min-width: 1440px)");
  const phone = !useMediaQuery("(min-width: 768px)");
  const [listOpen, setListOpen] = useState(false);
  useDismiss(listOpen && !wide, () => setListOpen(false));
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const shownReference = useReferenceEnemy();

  const user = listing.data?.user ?? [];
  const catalog = listing.data?.catalog ?? [];
  const current = selected ?? (autoOpen ? (user[0]?.code ?? null) : null);

  // Quitter l'ordre de bataille ouvert : la question commune du registre des saisies non enregistrées (M4).
  const guard = (action: () => void) => void leaveWith(action);
  const pick = (code: string) =>
    guard(() => {
      setSelected(code);
      setChosen(true);
      setListOpen(false);
    });
  const opened = (odb: OdbDetail, text: string) => {
    void queryClient.invalidateQueries({ queryKey: ["odbs"] });
    void queryClient.invalidateQueries({ queryKey: ["forces"] });
    queryClient.setQueryData(["odb", odb.code], odb);
    setSide(odb.side === "ENI" ? "ENI" : "AMI");
    setSelected(odb.code);
    setChosen(true);
    notify({ text, detail: unitsEditable(odb.name, odb.count ?? 0), tone: "ok" });
  };
  const duplicate = useMutation({
    mutationFn: ({ code, unit }: { code: string; unit: string | null }) => api.duplicateOdb(code, unit),
    onSuccess: (odb) => opened(odb, t("Copie créée")),
    onError: (e: Error) => notify({ text: t("Duplication impossible"), detail: e.message, tone: "error" }),
  });
  const imported = useMutation({
    mutationFn: (document: unknown) => api.importOdb(document),
    onSuccess: (odb) => opened(odb, t("Ordre de bataille importé")),
    onError: (e: Error) => notify({ text: t("Import impossible"), detail: e.message, tone: "error" }),
  });
  const importFile = async (file: File) => {
    try {
      imported.mutate(JSON.parse(await file.text()) as unknown);
    } catch {
      notify({
        text: t("Fichier illisible"),
        detail: t("Choisissez le fichier JSON d'un ordre de bataille (format de l'application Generex pour iPad)."),
        tone: "error",
      });
    }
  };
  const close = () => guard(() => (exerciseId ? setView("mission") : openWelcome()));
  const enemy = side === "ENI";
  // Dossier d'origine, champ suivant le camp ouvert : un ennemi créé depuis le lien des amis (camp basculé dans le
  // dialogue) s'emploie quand même, dans le champ de l'ennemi, et inversement.
  const sideOrigin = origin && { ...origin, field: enemy ? ("eni_odb" as const) : ("odb" as const) };
  // Ouverte depuis un dossier qui affronte l'ennemi générique (force non française, registre B39), ou sans lien
  // pendant qu'un tel dossier est ouvert (son nom est dans l'en-tête) : les ennemis des unités françaises n'y sont
  // pas montrés.
  const referenceEnemy = libraryReferenceEnemy(origin, shownReference);
  const switchSide = (next: OdbSide) =>
    next !== side &&
    guard(() => {
      setSide(next);
      setSelected(null);
      setChosen(false);
    });
  // « Employer dans le dossier » : le choix est reporté dans le brouillon des Données initiales (à enregistrer).
  const employ = (value: string, odb: OdbDetail) => {
    if (!sideOrigin) return;
    guard(() => {
      useOrbatsTarget.getState().employ({
        exerciseId: sideOrigin.exerciseId,
        field: sideOrigin.field,
        value,
        // Ennemi générique : aucun nom de faction, le champ « Ennemi » du dossier garde son choix (vide face à
        // l'ennemi générique).
        enemyType: referenceEnemy ? ((odb.faction_name as EnemyType | null | undefined) ?? null) : null,
      });
      setView("mission");
    });
  };
  const newOdb = (request: OrbatsCreate | true = true) => guard(() => setCreating(request));
  // Création demandée par le dossier, reprise par « Nouvel ordre de bataille » une fois le dialogue fermé.
  const creatingRequest = target.create && target.create.side === side ? target.create : null;
  const list = (
    <OdbList
      side={side}
      onSide={switchSide}
      loading={listing.isLoading}
      user={user}
      catalog={catalog}
      current={current}
      onPick={pick}
      referenceEnemy={referenceEnemy}
    />
  );

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view="orbats"
        context={exercise?.name}
        actions={
          phone ? (
            // Téléphone : l'import en icône, la création en libellé court.
            <>
              <IconButton
                size="sm"
                variant="outline"
                onClick={() => importInput.current?.click()}
                disabled={imported.isPending}
                title={t("Importer")}
                aria-label={t("Importer")}
              >
                {imported.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
              </IconButton>
              <Button size="sm" onClick={() => newOdb(enemy ? { side: "ENI", kind: "eni_bat" } : true)}>
                <Plus size={14} /> {t("Nouveau")}
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => importInput.current?.click()}
                disabled={imported.isPending}
                title={t("Fichier JSON d'un ordre de bataille de l'application Generex pour iPad (Gestion des unités)")}
              >
                {imported.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />} {t("Importer")}
              </Button>
              <Button size="sm" onClick={() => newOdb(enemy ? { side: "ENI", kind: "eni_bat" } : true)}>
                <Plus size={14} /> {enemy ? t("Nouvel ordre de bataille ennemi") : t("Nouvel ordre de bataille")}
              </Button>
            </>
          )
        }
        panelToggle={
          !wide && (
            <IconButton
              size="sm"
              onClick={() => setListOpen((o) => !o)}
              active={listOpen}
              title={t("Liste des ordres de bataille")}
              aria-label={t("Liste des ordres de bataille")}
            >
              <List size={16} />
            </IconButton>
          )
        }
        onClose={close}
      />
      <input
        ref={importInput}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) void importFile(file);
        }}
      />

      <div className="relative flex min-h-0 flex-1">
        {wide ? (
          <aside className={roomy ? "w-[300px] shrink-0 space-y-1 overflow-y-auto border-r border-line bg-canvas px-3 py-3" : "w-[280px] shrink-0 space-y-1 overflow-y-auto border-r border-line bg-canvas px-3 py-3"}>
            {list}
          </aside>
        ) : (
          <Presence show={listOpen}>
            {listOpen && (
              <>
                <Motion origin="fade" className="absolute inset-0 z-30 bg-ink/20" onClick={() => setListOpen(false)} aria-hidden />
                <Motion
                  origin="left"
                  className="absolute inset-y-0 left-0 z-40 w-[300px] max-w-[85%] space-y-1 overflow-y-auto border-r border-line bg-canvas px-3 py-3 shadow-float"
                >
                  {list}
                </Motion>
              </>
            )}
          </Presence>
        )}
        <main className="min-w-0 flex-1">
          {listing.isError || vocabulary.isError ? (
            <EmptyState variant="error">{(listing.error ?? vocabulary.error)?.message}</EmptyState>
          ) : !vocabulary.data || !listing.data ? (
            <EmptyState variant="loading" />
          ) : current ? (
            <OdbEditor
              key={current}
              code={current}
              vocabulary={vocabulary.data}
              origin={chosen ? sideOrigin : null}
              onDuplicate={(unit) => duplicate.mutate({ code: current, unit })}
              onDeleted={() => unsavedDrafts.bypass(() => setSelected(null))}
              onEmploy={employ}
            />
          ) : user.length > 0 ? (
            // Ouverte pour une création puis dialogue fermé : rien n'est choisi à la place de l'utilisateur.
            <EmptyState
              icon={enemy ? Swords : Network}
              title={t("Choisissez ou créez un ordre de bataille")}
              action={
                <span className="flex flex-wrap justify-center gap-2">
                  <Button highlight onClick={() => newOdb(creatingRequest ?? (enemy ? { side: "ENI", kind: "eni_bat" } : true))}>
                    <Plus size={14} /> {enemy ? t("Nouvel ordre de bataille ennemi") : t("Nouvel ordre de bataille")}
                  </Button>
                  {!wide && (
                    <Button variant="secondary" onClick={() => setListOpen(true)}>
                      <List size={14} /> {t("Liste des ordres de bataille")}
                    </Button>
                  )}
                </span>
              }
            >
              {origin?.exerciseName
                ? t("Choisissez-en un dans la liste pour le modifier, ou créez-en un pour le dossier « {name} ».", { name: origin.exerciseName })
                : t("Choisissez-en un dans la liste pour le modifier, ou créez-en un.")}
            </EmptyState>
          ) : enemy ? (
            <EmptyState
              icon={Swords}
              title={t("Créez votre premier ordre de bataille ennemi")}
              action={
                <Button highlight onClick={() => newOdb({ side: "ENI", kind: "eni_bat", start: "structure" })}>
                  <Shield size={14} /> {t("Créer un ordre de bataille ennemi")}
                </Button>
              }
              note={referenceEnemy ? t("Ou dupliquez une division de référence, à gauche, ou l'un de ses bataillons pourvus de subordonnés.") : undefined}
            >
              {referenceEnemy
                ? t(
                    "Construisez l'ennemi qui fait face au régiment (bataillon), à la brigade ou à la division : symboles hostiles, échelons, fonctions et rôles dans la manœuvre, à partir d'une structure ternaire générique ou d'une racine seule. Choisi dans les Données initiales, il remplace l'ennemi de référence sur la carte et dans l'ordre, qui cite ses codes.",
                  )
                : t(
                    "Construisez l'ennemi qui fait face au régiment (bataillon), à la brigade ou à la division : symboles hostiles, échelons, fonctions et rôles dans la manœuvre, à partir d'une structure ternaire générique ou d'une racine seule. Choisi dans les Données initiales, il détaille l'ennemi générique sur la carte et dans l'ordre, qui cite ses codes.",
                  )}
            </EmptyState>
          ) : (
            <EmptyState
              icon={Network}
              title={t("Créez votre premier ordre de bataille")}
              action={
                <span className="flex flex-wrap justify-center gap-2">
                  <Button highlight onClick={() => newOdb({ side: "AMI", kind: "gtia" })}>
                    <Plus size={14} /> {t("Partir de zéro")}
                  </Button>
                  <Button variant="secondary" onClick={() => newOdb({ side: "AMI", kind: "brigade", start: "copie" })}>
                    <Copy size={14} /> {t("Copier un ordre de bataille fourni")}
                  </Button>
                </span>
              }
              note={t("Ou importez un fichier de l'application Generex pour iPad.")}
            >
              {t(
                "Construisez un bataillon, une brigade ou une division de n'importe quelle nation de l'OTAN : échelon et fonction de chaque unité avec les symboles APP-6, numéros en bas à gauche et à droite. Il devient une force des Données initiales, placée sur la carte et commandée dans l'ordre comme celles fournies avec Generex.",
              )}
            </EmptyState>
          )}
        </main>
      </div>

      <Presence show={creating !== null && Boolean(amiVocabulary.data)}>
        {creating !== null && amiVocabulary.data && (
          <NewOdbDialog
            vocabulary={amiVocabulary.data}
            initialSide={side}
            initial={creating === true ? null : creating}
            referenceEnemy={referenceEnemy}
            onClose={() => setCreating(null)}
            onCreated={(odb) => {
              setCreating(null);
              opened(odb, t("Ordre de bataille créé"));
            }}
          />
        )}
      </Presence>
    </div>
  );
}
